import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import { CustomerType } from '../../../interfaces/IEvent'

interface AffectedSnippetProps {
  affectedName: string
  affectedType: string
  affectedCount: number | string
  onSnippetClick?: (type: CustomerType | string) => void
  defaultColor?: boolean
}

function AffectedSnippet({ affectedName, affectedType, affectedCount, onSnippetClick, defaultColor = false }: AffectedSnippetProps) {
  const theme = useTheme()

  const affectedColor = () => {
    if (defaultColor) return 'primary.main'

    if (typeof affectedCount === 'string' || affectedCount > 200) {
      return theme.palette.severity.critical
    } else if (affectedCount > 100) {
      return theme.palette.severity.high
    } else if (affectedCount > 50) {
      return theme.palette.severity.medium
    } else {
      return theme.palette.severity.low
    }
  }

  const affectedSnippetsIcon = (affectedType: CustomerType | string) => {
    switch (affectedType) {
      case CustomerType.Company:
        return <BusinessOutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
      case CustomerType.CompanyWithSla:
        return <KeyOutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
      case CustomerType.Private:
        return <Person4OutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
      case 'Telefon':
        return <LocalPhoneOutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
      case 'Epost':
        return <EmailOutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
      default:
        return <Groups3OutlinedIcon fontSize="large" sx={{ color: affectedColor() }} />
    }
  }

  const handleSnippetClick = (type: CustomerType | string) => {
    if (onSnippetClick) onSnippetClick(type)
  }

  return (
    <Box
      onClick={() => handleSnippetClick(affectedType)}
      sx={{
        border: 2.5,
        borderColor: affectedColor(),
        borderRadius: '8px',
        width: '100%',
        padding: 3,
        height: 'auto',
        backgroundColor: 'background.paper',

        '&:hover': {
          backgroundColor: 'background.default',
        },

        cursor: onSnippetClick ? 'pointer' : 'default',
      }}
    >
      <Stack
        direction={'column'}
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <IconButton size="small" disabled={!onSnippetClick} onClick={() => handleSnippetClick(affectedType)}>
          {affectedSnippetsIcon(affectedType)}
        </IconButton>
        <Typography
          variant="subtitle2"
          sx={{
            color: affectedColor(),
            fontSize: '1.5rem',
          }}
        >
          {affectedCount}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: affectedColor(),
            fontWeight: 600,
          }}
        >
          {affectedName}
        </Typography>
      </Stack>
    </Box>
  )
}

export default AffectedSnippet
